import React, { useState, useCallback, useEffect } from "react";
import { MathJax } from "better-react-mathjax";
import CorrectSvg from "@Assets/svg/Dashboard-svgs/correct.svg";
import WroungSvg from "@Assets/svg/Dashboard-svgs/Wroung.svg";
import { ReviewResultType } from "@Constants/app.constant";
import TimerSvg from "@Assets/svg/MockTest-svgs/Timer.svg"
import { ResultCard } from "../ResultCard/ResultCard";
import { useTranslation } from "react-i18next";
import { Skeleton } from "primereact/skeleton";

const SkeletonReviewCard = React.memo(({ review, index, showDataReviewType, totalQuestions }: any) => {
    const isVisible = (showDataReviewType == ReviewResultType.CORRECT_ANSWERS && review.AnsType == 1) ||
        (showDataReviewType == ReviewResultType.INCORRECT_ANSWERS && review.AnsType == 0) ||
        (showDataReviewType == ReviewResultType.ALL_ANSWERS);
    const { t } = useTranslation("common");

    return (
        <div className="question-review-card tw-opacity-60" style={{ display: isVisible ? 'block' : 'none' }}>
            <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center tw-justify-between tw-mb-4 tw-pr-4 tw-gap-3">
                <div className="rev-qust-no-head tw-flex tw-items-center tw-gap-2 tw-shrink-0">
                    <span className="tw-whitespace-nowrap">{t("question")} {index > 8 ? index + 1 : `0${index + 1}`}/{totalQuestions > 9 ? totalQuestions : `0${totalQuestions}`}</span>
                    <span className="pipe-span">
                        <svg width="2" height="18" viewBox="0 0 2 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1.78409 0.536932V17.2812H0.789773V0.536932H1.78409Z" fill="#667085" />
                        </svg>
                    </span>
                    <Skeleton width="4rem" height="1.2rem"></Skeleton>
                </div>
                <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-2 sm:tw-justify-end">
                    <Skeleton width="5rem" height="1.5rem"></Skeleton>
                    <Skeleton width="8rem" height="1.5rem"></Skeleton>
                </div>
            </div>
            <div className="qust-subject-card tw-pr-6 tw-mb-4">
                <Skeleton width="100%" height="2rem"></Skeleton>
            </div>
            <div className="reviw-opt-card tw-flex tw-flex-col tw-gap-2">
                <Skeleton width="50%" height="2.5rem"></Skeleton>
                <Skeleton width="60%" height="2.5rem"></Skeleton>
                <Skeleton width="45%" height="2.5rem"></Skeleton>
                <Skeleton width="55%" height="2.5rem"></Skeleton>
            </div>
        </div>
    );
});

const ReviewQuestionCard = React.memo(({
    review,
    index,
    showDataReviewType,
    testType,
    timePerQuestion,
    totalQuestions
}: any) => {
    const [isOpen, setIsOpen] = useState(false);
    const { t } = useTranslation("common");

    const onClickShowExplanation = () => {
        setIsOpen(prev => !prev);
    };

    const questionInsights = (time: number, answerType: number) => {
        const timeUtilization = timePerQuestion ? ((time / timePerQuestion) * 100) : 0;
        if (timeUtilization > 100) {
            if (answerType === 1) {
                return t("you_have_taken_too_much_time_for_this_question")
            } else if (answerType === 0) {
                return t("you're_overthinking_and_getting_stuck_on_questions")
            }
        } else if (timeUtilization >= 80 && timeUtilization <= 100) {
            if (answerType === 0) {
                return t("you_have_taken_proper_time_but_your_concepts_are_not_clear")
            }
        } else if (timeUtilization >= 50 && timeUtilization < 80) {
            if (answerType === 0) {
                return t("you_should_take_proper_time_to_answer")
            }

        } else if (timeUtilization >= 20 && timeUtilization < 50) {
            if (answerType === 1) {
                return t("excellent_speed_and_accuracy")
            } else if (answerType === 0) {
                return `${t("you_are_not_utilizing_the_time_properly")} ${t("causing_mistakes")}`
            }
        } else if (timeUtilization < 20) {
            if (answerType === 1) {
                return t("excellent_speed_and_accuracy")
            } else if (answerType === 0) {
                return `${t("you're_rushing")} ${t("causing_mistakes")}`
            }
        }
    };

    const checkNullExplaination = review.Explaination && review.Explaination.trim().length && review.Explaination.toLowerCase() != 'null';

    return (
        <div className="question-review-card" style={{ display: (showDataReviewType == ReviewResultType.CORRECT_ANSWERS && review.AnsType == 1) || (showDataReviewType == ReviewResultType.INCORRECT_ANSWERS && review.AnsType == 0) || (showDataReviewType == ReviewResultType.ALL_ANSWERS) ? 'block' : 'none' }} >
            <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center tw-justify-between tw-mb-4 tw-pr-4 tw-gap-3">
                <div className="rev-qust-no-head tw-flex tw-items-center tw-gap-2 tw-shrink-0">
                    <span className="tw-whitespace-nowrap">{t("question")} {index > 8 ? index + 1 : `0${index + 1}`}/{totalQuestions > 9 ? totalQuestions : `0${totalQuestions}`}</span>
                    <span className="pipe-span">
                        <svg width="2" height="18" viewBox="0 0 2 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1.78409 0.536932V17.2812H0.789773V0.536932H1.78409Z" fill="#667085" />
                        </svg>
                    </span>
                    <span className="quest-time-span tw-flex tw-items-center tw-gap-1 tw-shrink-0">
                        <img src={TimerSvg} className="timer-img" alt="timer-svg" />
                        {review.Time > 0 ? review.TimeToShow : '--:--'}
                    </span>
                </div>
                <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-2 sm:tw-justify-end">
                    <span className="tw-px-3 tw-py-1 tw-bg-violet-200 tw-border tw-border-violet-400 tw-rounded-md tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-black-700 tw-whitespace-nowrap">{t("level")} {review.DifficultyLevel}</span>
                    {review.ChapterName && review.ChapterName.toLowerCase() !== 'null' && (
                        <span className="tw-px-3 tw-py-1 tw-bg-blue-200 tw-border tw-border-blue-400 tw-rounded-md tw-text-[10px] sm:tw-text-xs tw-font-semibold tw-text-black-600">{t("chapter")}: {review.ChapterName}</span>
                    )}
                    {review.TopicName && review.TopicName.toLowerCase() !== 'null' && (
                        <span className="tw-px-3 tw-py-1 tw-bg-pink-200 tw-border tw-border-pink-400 tw-rounded-md tw-text-[10px] sm:tw-text-xs tw-font-semibold tw-text-black-600">{t("topic")}: {review.TopicName}</span>
                    )}
                </div>
            </div>
            <div className="qust-subject-card tw-pr-6">
                <div className="review-qust">
                    <MathJax dangerouslySetInnerHTML={{ __html: review.Question }} >
                    </MathJax>
                </div>
            </div>
            <div className="reviw-opt-card">
                {
                    review.QuestionTypeId == 3 ? (
                        <>
                            {review.SelectedOption != null && (
                                <div className={`review-options ${review.Ans == review.SelectedOption ? 'correct-question' : 'incorrect-qust'} tw-mb-2`}>
                                    <div className="opt-span">
                                        <span className="tw-flex">
                                            <span className="tw-mr-2 tw-font-medium">{`${t("your")} ${t("answer")}`}: </span>
                                            <span className="option-value" style={{ paddingLeft: 0 }}>
                                                {review.SelectedOption}
                                            </span>
                                        </span>
                                        {review.Ans != review.SelectedOption && (
                                            <img src={WroungSvg} className="incorrect-svg" alt="wrong-svg" />
                                        )}
                                        {review.Ans == review.SelectedOption && (
                                            <img className="Correct-svg" src={CorrectSvg} alt="correct-svg" />
                                        )}
                                    </div>
                                </div>
                            )}
                            {review.Ans != review.SelectedOption && (
                                <div className="review-options correct-question">
                                    <div className="opt-span">
                                        <span className="tw-flex">
                                            <span className="tw-mr-2 tw-font-medium">{`${t("correct")} ${t("answer")}`}:</span>
                                            <span className="option-value" style={{ paddingLeft: 0 }}>
                                                {review.Ans}
                                            </span>
                                        </span>
                                        <span className="correct-opt-span">{t("correct_answer_is")} {review.Ans}</span>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        Object.entries(review.Options).map((opt: any) => {
                            let rightOptions = review.Ans ? review.Ans.trim().toLowerCase().split(',') : [];
                            let options = review.SelectedOption ? review.SelectedOption.trim().toLowerCase().split(',') : [];
                            let missedOption = [];
                            if (review.IsMultiple) {
                                let matchedOption = rightOptions.filter((item: any) => {
                                    return options.indexOf(item) > -1
                                })
                                if (matchedOption && matchedOption.length) {
                                    missedOption = rightOptions.filter((item: any) => {
                                        return options.indexOf(item) === -1
                                    })
                                }
                            }
                            return (
                                <React.Fragment key={opt[0]}>
                                    {opt[1] && showDataReviewType != ReviewResultType.ALL_ANSWERS && ((review.Ans.trim().toLowerCase().includes(opt[0].toLowerCase())) || (review.SelectedOption && (review.SelectedOption.includes(opt[0])))) ?
                                        <ResultCard review={review} rightOptions={rightOptions} opt={opt} options={options} missedOption={missedOption} />
                                        :
                                        opt[1] && showDataReviewType == ReviewResultType.ALL_ANSWERS ?
                                            <ResultCard review={review} rightOptions={rightOptions} opt={opt} options={options} missedOption={missedOption} />
                                            : ""
                                    }
                                </React.Fragment>
                            )
                        })
                    )
                }
            </div>
            {review.AnsType === 1 && <div className="tw-flex tw-flex-col tw-mt-3">
                <span className="answer-status status-correct ">
                    {t("your_answer_is_correct")}
                </span>
                {
                    testType === 1 ? <span className="answer-status status-comment ">
                        {questionInsights(review.Time, 1)}
                    </span> : <>
                        {
                            review.TimeSolvingStatus === 3 && <span className="answer-status status-comment ">
                                {t("you_have_taken_too_much_time_for_this_question")}
                            </span>
                        }
                        {
                            review.TimeSolvingStatus === 1 && <span className="answer-status status-comment ">
                                {t("excellent_speed_and_accuracy")}
                            </span>
                        }
                    </>
                }
            </div>}

            {review.AnsType === 0 && <div className="tw-flex tw-flex-col tw-mt-3">
                <span className="answer-status status-incorrect">
                    {t("your_answer_is_incorrect")}
                </span>
                {
                    testType === 1 ? <span className="answer-status status-comment ">
                        {questionInsights(review.Time, 0)}
                    </span> : <>
                        {
                            review.TimeSolvingStatus === 1 && <span className="answer-status status-comment ">
                                {t("you're_rushing")} {t("causing_mistakes")}
                            </span>
                        }
                        {
                            review.TimeSolvingStatus === 3 && <span className="answer-status status-comment ">
                                {t("you're_overthinking_and_getting_stuck")}
                            </span>
                        }
                        {
                            review.TimeSolvingStatus === 2 && <span className="answer-status status-comment ">
                                {t("you_have_taken_proper_time_but_your_concepts_are_not_clear")}
                            </span>
                        }
                    </>
                }
            </div>}

            {review.AnsType === 2 && <div className="tw-mt-3 status-skipped">
                <span className="answer-status ">
                    {t("you_skipped_this_question")}
                </span>
            </div>}
            {checkNullExplaination ?
                <>
                    <div className="q-review-card">
                        <span className="button-links" onClick={onClickShowExplanation}>
                            {isOpen ? 'Hide Explanation' : 'Show Explanation'}
                        </span>
                    </div>
                    {isOpen && (
                        <div className="q-review-card-text visible" >
                            <MathJax dangerouslySetInnerHTML={{ __html: review.Explaination?.replace(/\r\n/g, '\n') }} dynamic={true} >
                            </MathJax>
                        </div>
                    )}
                </>
                : ''
            }
        </div>
    );
});

const ReviewResult = ({ Reviewdata, showDataReviewType, duration, questions, testType }: any) => {
    const timePerQuestion = questions > 0 ? (duration / questions) : 0;
    const [visibleCount, setVisibleCount] = useState(5);

    useEffect(() => {
        setVisibleCount(5);
    }, [Reviewdata]);

    useEffect(() => {
        if (visibleCount < Reviewdata.length) {
            const timer = setTimeout(() => {
                setVisibleCount(prev => Math.min(prev + 10, Reviewdata.length));
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [visibleCount, Reviewdata.length]);

    return (
        <>
            {Reviewdata.map((review: any, index: any) => {
                if (index < visibleCount) {
                    return (
                        <ReviewQuestionCard
                            key={index}
                            review={review}
                            index={index}
                            showDataReviewType={showDataReviewType}
                            testType={testType}
                            timePerQuestion={timePerQuestion}
                            totalQuestions={Reviewdata.length}
                        />
                    );
                } else {
                    return (
                        <SkeletonReviewCard
                            key={index}
                            review={review}
                            index={index}
                            showDataReviewType={showDataReviewType}
                            totalQuestions={Reviewdata.length}
                        />
                    );
                }
            })}
        </>
    );
};

export default React.memo(ReviewResult);